/**
 * Lot 10 — Expertise rare (1–2 experts ≥ seuil).
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { analyticsApi, ApiRequestError, type RareExpertiseDto } from "../../api/api";
import {
  AnalyticsFilterBar,
  lineTitle,
  type AnalyticsFiltersState,
} from "../../components/ui/AnalyticsFilterBar";
import { PageHeader } from "../../components/ui/PageHeader";

export function RareExpertisePage() {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<AnalyticsFiltersState>({
    year: new Date().getFullYear(),
  });
  const [data, setData] = useState<RareExpertiseDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void analyticsApi
      .rareExpertise(filters)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err) => {
        if (!cancelled) {
          setData(null);
          setError(err instanceof ApiRequestError ? err.message : t("analytics_load_failed"));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filters, t]);

  return (
    <div className="sm-page">
      <PageHeader title={t("rare_expertise")} subtitle={t("rare_expertise_sub")} />
      <AnalyticsFilterBar filters={filters} onChange={setFilters} />
      {error ? <p className="sm-error">{error}</p> : null}
      {loading ? <p className="sm-muted">{t("loading")}</p> : null}

      {!loading && data ? (
        <>
          <p className="sm-muted">
            {t("target_threshold")}: <strong>{data.target}</strong>
            {" · "}
            {data.summary.single} {t("rare_single")} · {data.summary.rare} {t("rare_two")} ·{" "}
            {data.summary.critical} {t("critical")}
          </p>
          <div className="sm-card">
            <div className="sm-table-wrap">
              <table className="sm-table">
                <thead>
                  <tr>
                    <th>Line</th>
                    <th>≥ {t("target_threshold")}</th>
                    <th>{t("status")}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="sm-muted" style={{ textAlign: "center", padding: 24 }}>
                        {t("rare_none")}
                      </td>
                    </tr>
                  ) : (
                    data.items.map((item) => {
                      const { title, subtitle } = lineTitle(item.line);
                      return (
                        <tr key={item.line.id}>
                          <td>
                            <div>{title}</div>
                            <div className="sm-muted" style={{ fontSize: 11 }}>
                              {subtitle} — {item.line.domain}
                            </div>
                          </td>
                          <td style={{ textAlign: "center" }}>
                            {item.aboveTarget}/{item.total}
                          </td>
                          <td>
                            <span
                              className={`sm-badge-pill ${
                                item.risk === "single" ? "sm-badge-yellow" : "sm-badge-gray"
                              }`}
                            >
                              {item.risk === "single" ? t("rare_single") : t("rare_two")}
                            </span>
                            {item.lineIsCritical ? (
                              <span
                                className="sm-badge-pill sm-badge-red"
                                style={{ marginLeft: 6 }}
                              >
                                {t("critical")}
                              </span>
                            ) : null}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
